import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './kilimanjaro.css';
import { IMAGE_FALLBACK, KILIMANJARO_HERO_SLIDES, KILIMANJARO_PAGE_IMAGES } from '../../data/destinationImages';

const KILI_IMAGES = KILIMANJARO_PAGE_IMAGES;

const HERO_STATS = [
  { value: '5,895 m', label: 'Uhuru Peak', delay: 0 },
  { value: '7', label: 'Climate zones', delay: 80 },
  { value: '6–9', label: 'Days to summit', delay: 160 },
  { value: 'UNESCO', label: 'World Heritage Site', delay: 240 },
];

const HIGHLIGHTS = [
  {
    iconImage: KILI_IMAGES.summit,
    title: 'Uhuru Peak',
    text: 'Stand on the roof of Africa at 5,895 metres glaciers, crater rim views, and a sunrise you will never forget.',
    facePosition: 'center 35%',
  },
  {
    iconImage: KILI_IMAGES.rainforest,
    title: 'Rainforest Zone',
    text: 'Trek through lush montane forest on the lower slopes colobus monkeys, exotic birds, and misty canopy trails.',
    facePosition: 'center 40%',
  },
  {
    iconImage: KILI_IMAGES.moorland,
    title: 'Heath & Moorland',
    text: 'Giant groundsels and lobelias dot the landscape as you climb above the tree line into an otherworldly high-altitude world.',
    facePosition: 'center 38%',
  },
  {
    iconImage: KILI_IMAGES.route,
    title: 'Legendary Routes',
    text: 'Choose Machame, Lemosho, Marangu, Rongai, or Northern Circuit each with distinct scenery and acclimatisation profiles.',
    facePosition: 'center 42%',
  },
  {
    iconImage: KILI_IMAGES.camps,
    title: 'Mountain Camps',
    text: 'Licensed guides and porters support every step comfortable tented camps and huts positioned for safe, steady acclimatisation.',
    facePosition: 'center 45%',
  },
  {
    iconImage: KILI_IMAGES.summit,
    title: 'Summit Night',
    text: 'The final push under starlight headlamps tracing a line of climbers toward the crater rim as dawn breaks over the African plains.',
    facePosition: 'center 30%',
  },
];

const SEASONS = [
  {
    badge: 'peak',
    title: 'Dry Season I',
    months: 'January – March',
    text: 'Warm, clear conditions and excellent visibility one of the best windows for summit success and photography.',
  },
  {
    badge: 'peak',
    title: 'Dry Season II',
    months: 'June – October',
    text: 'Cooler and drier with stable weather peak climbing season with the highest trekker traffic on popular routes.',
  },
  {
    badge: 'shoulder',
    title: 'Shoulder Season',
    months: 'November & May',
    text: 'Transition months with mixed weather. Fewer crowds and lower rates still achievable with the right route and pacing.',
  },
];

const EXPERIENCES = [
  {
    image: KILI_IMAGES.route,
    title: 'Machame Route',
    text: 'The "Whiskey Route" six to seven days through diverse zones with strong acclimatisation and dramatic scenery.',
  },
  {
    image: KILI_IMAGES.camps,
    title: 'Lemosho Route',
    text: 'A longer, quieter approach from the west eight days for better acclimatisation and higher summit success rates.',
  },
  {
    image: KILI_IMAGES.overview,
    title: 'Safari & Kili Combo',
    text: 'Pair your climb with a Northern Circuit safari Ngorongoro, Tarangire, and Serengeti before or after the mountain.',
  },
];

const GALLERY_IMAGES = [
  { src: KILI_IMAGES.mountain, caption: 'Mount Kilimanjaro' },
  { src: KILI_IMAGES.rainforest, caption: 'Rainforest View' },
  { src: KILI_IMAGES.moorland, caption: 'High Moorland' },
  { src: KILI_IMAGES.route, caption: 'Trekking Route' },
  { src: KILI_IMAGES.summit, caption: 'Summit Views' },
  { src: KILI_IMAGES.camps, caption: 'Mountain Camp' },
];

const TREK_ROUTES = [
  {
    badge: 'popular',
    badgeLabel: 'Most popular',
    title: 'Machame Route',
    duration: '6–7 days',
    difficulty: 'Moderate',
    success: 'High',
    text: 'The classic "Whiskey Route" varied terrain, excellent acclimatisation, and some of the mountain\'s most dramatic scenery.',
    highlights: ['South-west approach', 'Barranco Wall', 'Shira Plateau'],
  },
  {
    badge: 'recommended',
    badgeLabel: 'Recommended',
    title: 'Lemosho Route',
    duration: '7–8 days',
    difficulty: 'Moderate',
    success: 'Very high',
    text: 'A longer western approach with fewer crowds widely regarded as the best route for acclimatisation and summit success.',
    highlights: ['Remote trailhead', 'Rainforest to alpine desert', 'Steady altitude gain'],
  },
  {
    badge: 'classic',
    badgeLabel: 'Classic',
    title: 'Marangu Route',
    duration: '5–6 days',
    difficulty: 'Moderate',
    success: 'Good',
    text: 'The only route with hut accommodation a gentler gradient and the same path up and down, ideal for first-time trekkers.',
    highlights: ['Shared mountain huts', 'Coca-Cola route', 'Direct south-east ascent'],
  },
  {
    badge: 'quiet',
    badgeLabel: 'Quiet trail',
    title: 'Rongai Route',
    duration: '6–7 days',
    difficulty: 'Moderate',
    success: 'High',
    text: 'Approaches from the north near the Kenyan border drier conditions and a quieter trail with a different perspective on Kibo.',
    highlights: ['Northern wilderness', 'Drier climate', 'Wildlife on lower slopes'],
  },
  {
    badge: 'premium',
    badgeLabel: 'Best acclimatisation',
    title: 'Northern Circuit',
    duration: '8–9 days',
    difficulty: 'Moderate',
    success: 'Highest',
    text: 'The longest route circles the mountain via remote northern slopes maximum time to acclimatise before the summit push.',
    highlights: ['Full mountain circuit', 'Remote northern camps', 'Highest success rates'],
  },
  {
    badge: 'challenging',
    badgeLabel: 'Most demanding',
    title: 'Umbwe Route',
    duration: '5–6 days',
    difficulty: 'Strenuous',
    success: 'Lower',
    text: 'The steepest and shortest route fast altitude gain suited to experienced hikers seeking a direct, demanding challenge.',
    highlights: ['Steep forest ridge', 'Shortest timeline', 'For fit, experienced trekkers'],
  },
];

const GETTING_THERE_ROUTES = [
  'Fly into Kilimanjaro International Airport (JRO) the main gateway, 45 minutes from Moshi and 1 hour from Arusha',
  'Most climbs start from Moshi or Arusha with a pre-trek briefing, gear check, and transfer to the trailhead',
  'Combine with a Northern Circuit safari JRO sits between the mountain and Tanzania\'s premier wildlife parks',
  'Allow one rest day before your climb and one recovery day after altitude and travel both demand pacing',
];

function Lightbox({ image, onClose }) {
  if (!image) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="kilimanjaro-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="kilimanjaro-lightbox__close"
        aria-label="Close image viewer"
        onClick={onClose}
      >
        ✕
      </button>
      <img
        className="kilimanjaro-lightbox__img"
        src={image.src}
        alt={image.caption}
      />
      <span className="kilimanjaro-lightbox__caption">{image.caption}</span>
    </div>
  );
}

function Kilimanjaro() {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="kilimanjaro">
      <section className="kilimanjaro-hero" aria-labelledby="kilimanjaro-hero-heading">
        <div className="kilimanjaro-hero__media" aria-hidden="true">
          <HeroSlideshow classPrefix="kilimanjaro-hero" slides={KILIMANJARO_HERO_SLIDES} fallback={IMAGE_FALLBACK} />
          <div className="kilimanjaro-hero__overlay" />
        </div>
        <div className="page-container kilimanjaro-hero__container">
          <div className="kilimanjaro-hero__content">
            <nav className="kilimanjaro-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="kilimanjaro-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <a href="#trekking">Trekking</a>
              <span className="kilimanjaro-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Kilimanjaro</span>
            </nav>
            <p className="kilimanjaro-hero__eyebrow">Northern Tanzania</p>
            <h1 id="kilimanjaro-hero-heading">
              Mount
              <span>Kilimanjaro</span>
            </h1>
            <p className="kilimanjaro-hero__lead">
              Africa&apos;s highest peak, a multi-day trek through rainforest,
              moorland, and alpine desert to the snow-capped roof of the continent.
            </p>
            <div className="kilimanjaro-hero__actions">
              <a className="kilimanjaro-btn kilimanjaro-btn--primary" href="#contact">
                Plan your Kilimanjaro trek
              </a>
              <a className="kilimanjaro-btn kilimanjaro-btn--outline" href="#kilimanjaro-gallery">
                View gallery
              </a>
            </div>
          </div>
        </div>
        <div className="kilimanjaro-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="kilimanjaro-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="kilimanjaro-hero__stat-value">{stat.value}</span>
              <span className="kilimanjaro-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="kilimanjaro-overview"
        className="kilimanjaro-section kilimanjaro-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="kilimanjaro-overview__grid">
            <Reveal className="kilimanjaro-overview__text">
              <p className="kilimanjaro-section__eyebrow">About the mountain</p>
              <h2 id="overview-heading">The roof of Africa</h2>
              <p>
                Mount Kilimanjaro is a free-standing volcanic massif in
                north-eastern Tanzania the highest point on the African
                continent and one of the world&apos;s most accessible high-altitude
                treks. No technical climbing is required, but the altitude
                demands respect, preparation, and time to acclimatise.
              </p>
              <p>
                The mountain rises through seven distinct ecological zones,
                from cultivated foothills to equatorial rainforest, heath,
                alpine desert, and finally the arctic summit cap. With the
                right route, guide team, and pacing, reaching Uhuru Peak is
                one of travel&apos;s great achievements.
              </p>
              <ul className="kilimanjaro-overview__details">
                <Reveal as="li" className="kilimanjaro-overview__detail" delay={0}>
                  <span className="kilimanjaro-overview__detail-label">Location</span>
                  <span className="kilimanjaro-overview__detail-value">Kilimanjaro Region</span>
                </Reveal>
                <Reveal as="li" className="kilimanjaro-overview__detail" delay={80}>
                  <span className="kilimanjaro-overview__detail-label">Summit</span>
                  <span className="kilimanjaro-overview__detail-value">Uhuru Peak, 5,895 m</span>
                </Reveal>
                <Reveal as="li" className="kilimanjaro-overview__detail" delay={160}>
                  <span className="kilimanjaro-overview__detail-label">Duration</span>
                  <span className="kilimanjaro-overview__detail-value">6–9 days typical</span>
                </Reveal>
                <Reveal as="li" className="kilimanjaro-overview__detail" delay={240}>
                  <span className="kilimanjaro-overview__detail-label">Best for</span>
                  <span className="kilimanjaro-overview__detail-value">Summit trekking</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="kilimanjaro-overview__image" variant="right" delay={200}>
              <SafeImage
                className="kilimanjaro-overview__img"
                src={KILI_IMAGES.overview}
                alt="Mount Kilimanjaro high-altitude landscape"
              />
              <span className="kilimanjaro-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      <section
        id="kilimanjaro-highlights"
        className="kilimanjaro-section kilimanjaro-wildlife"
        aria-labelledby="highlights-heading"
      >
        <div className="page-container">
          <Reveal className="kilimanjaro-section__header">
            <p className="kilimanjaro-section__eyebrow">Trek highlights</p>
            <h2 id="highlights-heading">Life on the mountain</h2>
            <p className="kilimanjaro-section__intro">
              From rainforest trails to glacier views six defining moments
              on the path to Uhuru Peak.
            </p>
          </Reveal>
          <ul className="kilimanjaro-wildlife__grid">
            {HIGHLIGHTS.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="kilimanjaro-wildlife__card"
                delay={index * 100}
                variant="scale"
              >
                <div className="kilimanjaro-wildlife__icon-frame">
                  <span className="kilimanjaro-wildlife__icon-ring" aria-hidden="true" />
                  <span
                    className="kilimanjaro-wildlife__icon"
                    style={{ '--wildlife-face-position': item.facePosition }}
                  >
                    <img src={item.iconImage} alt={item.title} />
                  </span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="kilimanjaro-gallery"
        className="kilimanjaro-section kilimanjaro-gallery"
        aria-labelledby="gallery-heading"
      >
        <div className="page-container">
          <Reveal className="kilimanjaro-section__header">
            <p className="kilimanjaro-section__eyebrow">Photo gallery</p>
            <h2 id="gallery-heading">Scenes from Kilimanjaro</h2>
            <p className="kilimanjaro-section__intro">
              From misty forest paths to the crater rim at dawn the
              mountain in every climate zone.
            </p>
          </Reveal>
          <div className="kilimanjaro-gallery__grid">
            {GALLERY_IMAGES.map((img, index) => (
              <Reveal
                key={img.caption}
                className="kilimanjaro-gallery__item"
                delay={index * 100}
                variant="scale"
                onClick={() => setLightboxImage(img)}
                role="button"
                tabIndex={0}
                aria-label={`View ${img.caption}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxImage(img);
                  }
                }}
              >
                <SafeImage src={img.src} alt={img.caption} />
                <span className="kilimanjaro-gallery__caption">{img.caption}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        id="kilimanjaro-routes"
        className="kilimanjaro-section kilimanjaro-routes"
        aria-labelledby="routes-heading"
      >
        <div className="page-container">
          <Reveal className="kilimanjaro-section__header">
            <p className="kilimanjaro-section__eyebrow">Choose your path</p>
            <h2 id="routes-heading">Kilimanjaro trekking routes</h2>
            <p className="kilimanjaro-section__intro">
              Six official routes to Uhuru Peak compare duration, difficulty,
              and scenery to find the climb that matches your goals.
            </p>
          </Reveal>
          <ul className="kilimanjaro-routes__grid">
            {TREK_ROUTES.map((route, index) => (
              <Reveal
                key={route.title}
                as="li"
                className="kilimanjaro-routes__card"
                delay={index * 80}
              >
                <span
                  className={`kilimanjaro-routes__badge kilimanjaro-routes__badge--${route.badge}`}
                >
                  {route.badgeLabel}
                </span>
                <h3>{route.title}</h3>
                <dl className="kilimanjaro-routes__meta">
                  <div>
                    <dt>Duration</dt>
                    <dd>{route.duration}</dd>
                  </div>
                  <div>
                    <dt>Difficulty</dt>
                    <dd>{route.difficulty}</dd>
                  </div>
                  <div>
                    <dt>Success rate</dt>
                    <dd>{route.success}</dd>
                  </div>
                </dl>
                <p>{route.text}</p>
                <ul className="kilimanjaro-routes__highlights">
                  {route.highlights.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      <section
        id="kilimanjaro-seasons"
        className="kilimanjaro-section kilimanjaro-seasons"
        aria-labelledby="seasons-heading"
      >
        <div className="page-container">
          <Reveal className="kilimanjaro-section__header">
            <p className="kilimanjaro-section__eyebrow">Plan your timing</p>
            <h2 id="seasons-heading">Best time to climb Kilimanjaro</h2>
            <p className="kilimanjaro-section__intro">
              Dry seasons offer the best summit conditions choose your window
              for weather, crowds, and acclimatisation.
            </p>
          </Reveal>
          <ul className="kilimanjaro-seasons__grid">
            {SEASONS.map((season, index) => (
              <Reveal
                key={season.title}
                as="li"
                className="kilimanjaro-seasons__card"
                delay={index * 120}
              >
                <span
                  className={`kilimanjaro-seasons__card-badge kilimanjaro-seasons__card-badge--${season.badge}`}
                >
                  {season.badge === 'peak'
                    ? '★ Peak Season'
                    : season.badge === 'green'
                      ? '🌿 Green Season'
                      : '◐ Shoulder Season'}
                </span>
                <h3>{season.title}</h3>
                <span className="kilimanjaro-seasons__card-months">{season.months}</span>
                <p>{season.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="kilimanjaro-experiences"
        className="kilimanjaro-section kilimanjaro-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="kilimanjaro-section__header">
            <p className="kilimanjaro-section__eyebrow">Safari combinations</p>
            <h2 id="experiences-heading">Extend your adventure</h2>
            <p className="kilimanjaro-section__intro">
              Pair your summit bid with game drives and lodge stays the
              classic Tanzania trek-and-safari combination.
            </p>
          </Reveal>
          <ul className="kilimanjaro-experiences__grid">
            {EXPERIENCES.map((exp, index) => (
              <Reveal
                key={exp.title}
                as="li"
                className="kilimanjaro-experiences__card"
                delay={index * 120}
              >
                <div className="kilimanjaro-experiences__card-image">
                  <SafeImage src={exp.image} alt={exp.title} />
                </div>
                <div className="kilimanjaro-experiences__card-body">
                  <h3>{exp.title}</h3>
                  <p>{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="kilimanjaro-getting-there"
        className="kilimanjaro-section kilimanjaro-getting-there"
        aria-labelledby="getting-there-heading"
      >
        <div className="page-container">
          <div className="kilimanjaro-getting-there__inner">
            <Reveal className="kilimanjaro-getting-there__copy">
              <p className="kilimanjaro-section__eyebrow">Getting there</p>
              <h2 id="getting-there-heading">How to reach Kilimanjaro</h2>
              <p>
                Kilimanjaro International Airport (JRO) serves Moshi and
                Arusha, the two base towns for every climb. Most operators
                include airport transfers, pre-trek accommodation, and
                transport to your chosen trailhead.
              </p>
              <ul className="kilimanjaro-getting-there__routes">
                {GETTING_THERE_ROUTES.map((route) => (
                  <li key={route}>{route}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="kilimanjaro-getting-there__map" variant="right" delay={200}>
              <iframe
                title="Mount Kilimanjaro location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255168.089654321!2d37.35!3d-3.067!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x183b8f9a8f9a8f9a%3A0x1234567890abcdef!2sMount%20Kilimanjaro!5e0!3m2!1sen!2stz!4v1700000000000!5m2!1sen!2stz"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="kilimanjaro-cta" aria-labelledby="kilimanjaro-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="kilimanjaro-cta__inner">
              <h2 id="kilimanjaro-cta-heading">
                Ready to stand on the roof of Africa?
              </h2>
              <p>
                Tell us your travel dates and fitness level we&apos;ll design a
                tailor-made Kilimanjaro trek with licensed guides, porters,
                and the route that fits you best.
              </p>
              <a
                className="kilimanjaro-btn kilimanjaro-btn--primary kilimanjaro-btn--lg"
                href="#contact"
              >
                Get a free Kilimanjaro quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Kilimanjaro;
